import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import Button from "../../common/Button";
import api from "../../../api";
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';

export default function PendingStudentRequest() {
  const [pendingStudents, setPendingStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editedStudent, setEditedStudent] = useState({
    name: '',
    email: '',
    address: ''
  });
  const [selectedIds, setSelectedIds] = useState([]); // Track selected student IDs for multi-select

  // Fetch pending students
  useEffect(() => {
    const getApiData = async () => {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}students/pending`
      );
      setPendingStudents(data);
    };
    getApiData();
  }, []);

  // Approve selected students
  const handleApproveMultiple = async () => {
    try {
      for (const id of selectedIds) {
        await api.put(`${process.env.REACT_APP_API_BASE_URL}students/approve/${id}`);
      }
      alert("Selected Students Approved Successfully!");
      // Update the list of pending students
      setPendingStudents((prev) =>
        prev.filter((student) => !selectedIds.includes(student._id))
      );
      setSelectedIds([]); // Clear selected IDs after approval
    } catch (error) {
      console.error("Approval Failed:", error);
    }
  };

  // Approve individual student
  const handleApprove = async (id) => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}students/approve/${id}`);
      alert("Student Approved Successfully!");
      setPendingStudents((prev) => prev.filter((student) => student._id !== id));
    } catch (error) {
      console.error("Approval Failed:", error);
    }
  };

  // Delete Student
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this student?");
    if (!confirmDelete) return;

    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}students/delete/${id}`);
      alert("Student Deleted Successfully!");
      setPendingStudents((prev) => prev.filter((student) => student._id !== id));
    } catch (error) {
      console.error("Delete Failed:", error);
    }
  };

  // Open the Edit Modal and populate it with the selected student's data
  const handleEdit = async (id) => {
    const student = pendingStudents.find(student => student._id === id);
    setSelectedStudent(student);
    setEditedStudent({
      name: student.name,
      email: student.email,
      address: student.address
    });
    setOpenEditModal(true);
  };

  // Handle the changes in the edit form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedStudent((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  // Submit the updated student details
  const handleSubmit = async () => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}students/edit/${selectedStudent._id}`, editedStudent);
      alert("Student Updated Successfully!");
      setPendingStudents((prev) => 
        prev.map((student) => 
          student._id === selectedStudent._id ? { ...student, ...editedStudent } : student
        )
      );
      setOpenEditModal(false);
    } catch (error) {
      console.error("Edit Failed:", error);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      minWidth: 150,
      headerClassName: "custom-header",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 3,
      minWidth: 200,
      headerClassName: "custom-header",
    },
    {
      field: "address",
      headerName: "Address",
      flex: 3,
      minWidth: 100,
      headerClassName: "custom-header",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 3,
      minWidth: 200,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <div className="flex space-x-2">
          <Button onClick={() => handleApprove(params.row._id)}>Approve</Button>
          <Button onClick={() => handleEdit(params.row._id)}>Edit</Button>
          <Button onClick={() => handleDelete(params.row._id)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
      <div className="flex justify-between mb-4">
        {/* Button for approving multiple students */}
        <Button onClick={handleApproveMultiple} disabled={selectedIds.length === 0}>
          Approve Selected
        </Button>
      </div>

      <DataGrid
        rows={pendingStudents || []}
        columns={columns}
        getRowId={(row) => row._id}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        pageSizeOptions={[5, 10]}
        checkboxSelection
        onSelectionModelChange={(newSelection) => {
          setSelectedIds(newSelection); // Update selected IDs state
        }}
        sx={{
          // Glassy root container
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "none",
          borderRadius: 3,
          backdropFilter: "blur(8px)",
        
          // Top header row
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
          },
        
          // Footer container (bottom row)
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "rgba(255,255,255,0.05)",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          },
        
          // Ensure rows have no override from CSS variable
          "& .MuiDataGrid-row": {
            backgroundColor: "transparent !important",
          },
        
          // Optional: override containerBackground if needed
          "--DataGrid-containerBackground": "transparent",
        }}
      />

      {/* Edit Modal */}
      <Modal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        aria-labelledby="edit-student-modal"
        aria-describedby="edit-student-modal-description"
      >
        <Box sx={{ width: 400, padding: 2, backgroundColor: 'white', margin: 'auto', marginTop: '10%' }}>
          <h2>Edit Student</h2>
          <TextField
            label="Name"
            name="name"
            value={editedStudent.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={editedStudent.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={editedStudent.address}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <div className="flex justify-between mt-3">
            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Save</Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
